import React, { Suspense, useContext, useEffect, useMemo } from 'react'

import { Alert } from 'antd'
import { I18nContext, useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import useSWR from 'swr'

import { getClusterInfo, getComparisonReport } from '~/api'
import LoadingSpin from '~/components/LoadingSpin'
import { Header } from '~/componentv2/Header'
import { useCurOrg } from '~/hooks/state'
import { RemoteNamespace } from '~/translations'

import ReportTable from '../components/Table'
import { OrgAndClusterContext, TimeRangeContext } from '../shared/context'
import { COMPARISON_REPORT_NS } from '../translations'
import { LogTable } from './LogTable'
import { tables } from './tableDef'

function getProp(object: any, keys: string | string[], defaultVal?: any): any {
  keys = Array.isArray(keys) ? keys : keys.split('.')
  object = object[keys[0]]
  if (object && keys.length > 1) {
    return getProp(object, keys.slice(1))
  }
  return object === undefined ? defaultVal : object
}

const ComparisonReport: React.FC = () => {
  const { t } = useTranslation(COMPARISON_REPORT_NS)
  const { orgId, cid, rid } = useParams<'orgId' | 'cid' | 'rid'>()
  const { data: clusterInfoRes, error: clusterError } = useSWR(
    `/orgs/${orgId}/clusters/${cid}`,
    () => getClusterInfo(orgId!, cid!)
  )
  const { data, error } = useSWR(
    ['comparison_report/detail', orgId, cid, rid],
    () => getComparisonReport(orgId!, cid!, rid!)
  )
  const d = data?.data?.data as any

  const { curOrg } = useCurOrg()
  const { orgType } = curOrg!

  const orgAndCluster = useMemo(
    () => ({
      isLoading: (!clusterInfoRes && !clusterError) || !curOrg,
      orgId: curOrg?.id,
      orgName: curOrg?.name,
      orgType,
      clusterId: clusterInfoRes?.data.id,
      clusterName: clusterInfoRes?.data.name
    }),
    [curOrg, clusterInfoRes, clusterError, orgType]
  )

  return (
    <OrgAndClusterContext.Provider value={orgAndCluster}>
      <div className='h-full flex flex-col overflow-auto'>
        <Header i18n />

        <main className='flex-auto m-auto w-full h-full py-6'>
          <article className='px-8 py-8 min-h-full bg-white'>
            <header className='mb-8'>
              <h1 className='text-4xl font-semibold'>{t('title')}</h1>
            </header>

            {(error || (d as any)?.errorMessage) && (
              <Alert
                message={
                  (d as any)?.errorMessage ||
                  error?.response?.data?.message ||
                  error.message
                }
                type='error'
                showIcon
                className='mb-4'
              />
            )}

            {!data && !error ? (
              <LoadingSpin />
            ) : (
              !!d && (
                <TimeRangeContext.Provider
                  value={d.comparisonTimeRange[0].values}
                >
                  {tables.map(({ columns, key, subTables, hideExpandData }) => (
                    <section className='mb-8' key={key}>
                      <h2 className='mb-6'>{t(`section.${key}`)}</h2>
                      {columns?.length &&
                        (d[key] ? (
                          <ReportTable
                            dataSource={d[key]}
                            columns={columns}
                            expandable={{
                              rowExpandable: (rowData) =>
                                !!rowData.subValues?.length && !hideExpandData
                            }}
                          />
                        ) : (
                          <Alert type='warning' message='No data' />
                        ))}
                      {subTables?.length &&
                        subTables.map(
                          ({
                            columns: subColumns,
                            key: subKey,
                            hideExpandData
                          }) => (
                            <section className='mb-6' key={subKey}>
                              <h3 className='mb-2'>{t(`${key}.${subKey}`)}</h3>
                              {getProp(d, subKey) ? (
                                <ReportTable
                                  dataSource={getProp(d, subKey)}
                                  columns={subColumns!}
                                  expandable={{
                                    rowExpandable: (rowData) =>
                                      !!rowData.subValues?.length &&
                                      !hideExpandData
                                  }}
                                />
                              ) : (
                                <Alert type='warning' message='No data' />
                              )}
                            </section>
                          )
                        )}
                    </section>
                  ))}
                  <section className='mb-8'>
                    <h2 className='mb-6'>{t(`section.logComparison`)}</h2>
                    <LogTable
                      data={d.logComparison?.map((d: any) => d.values) || []}
                    />
                  </section>
                </TimeRangeContext.Provider>
              )
            )}
          </article>
        </main>
      </div>
    </OrgAndClusterContext.Provider>
  )
}

const Page: React.FC = () => {
  const { i18n } = useContext(I18nContext) || {}

  // Load i18n remote resources for comparison report
  useEffect(() => {
    i18n?.loadNamespaces([RemoteNamespace.MetricsCompare])
  }, [i18n])

  return (
    <Suspense fallback={<LoadingSpin />}>
      <ComparisonReport />
    </Suspense>
  )
}

export default Page
